<template>
  <v-app
    class="gc-app theme--dark"
    :class="{ 'gc-app--authenticated': !!user }"
  >
    <div
      v-if="bootLoading"
      class="loading-overlay active"
    >
      <img alt="Gaming Corps" class="loading-logo" src="@/assets/logo.png">
      <div class="spinner" />
    </div>

    <translation-ai-overlay
      v-if="user"
      :visible="aiTranslationOverlay.visible"
      :message="aiTranslationOverlay.message"
      :detail="aiTranslationOverlay.detail"
    />

    <login-screen
      v-if="!bootLoading && !user"
      :message="loginMessage"
      :login-url="loginPageUrl"
    />

    <template v-else-if="user">
      <v-app-bar app dark flat class="gc-topbar" height="64">
        <div class="gc-topbar-brand">
          <v-img
            alt="Gaming Corps"
            class="gc-topbar-logo"
            contain
            src="@/assets/logo.png"
            width="132"
          />
        </div>

        <v-spacer />

        <button
          type="button"
          class="gc-topbar-dashboard"
          @click="goToDashboard"
        >
          <v-icon small color="#f5c347">mdi-arrow-left</v-icon>
          <span>Back to Dashboard</span>
        </button>

        <v-menu
          offset-y
          left
          nudge-bottom="8"
          content-class="gc-topbar-user-menu"
        >
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="gc-topbar-user"
              v-bind="attrs"
              v-on="on"
            >
              <v-avatar class="gc-topbar-avatar" size="40">
                <v-img
                  v-if="showProfilePhoto"
                  :src="user.picture"
                  :alt="userDisplayName"
                  cover
                  referrerpolicy="no-referrer"
                  @error="avatarLoadFailed = true"
                />
                <span v-else class="gc-topbar-avatar-fallback">
                  {{ userInitials }}
                </span>
              </v-avatar>
              <span class="gc-topbar-user-meta d-none d-sm-flex">
                <span class="gc-topbar-user-name">{{ userDisplayName }}</span>
                <span v-if="user.email" class="gc-topbar-user-email">{{ user.email }}</span>
              </span>
              <v-icon class="gc-topbar-user-chevron d-none d-sm-inline" small>
                mdi-chevron-down
              </v-icon>
            </button>
          </template>

          <v-list dense dark class="py-0">
            <v-list-item class="gc-topbar-user-menu-header">
              <v-list-item-avatar size="48" class="gc-topbar-avatar gc-topbar-avatar--menu">
                <v-img
                  v-if="showProfilePhoto"
                  :src="user.picture"
                  :alt="userDisplayName"
                  cover
                  referrerpolicy="no-referrer"
                />
                <span v-else class="gc-topbar-avatar-fallback">{{ userInitials }}</span>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="white--text font-weight-medium">
                  {{ userDisplayName }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="user.email" class="gc-topbar-user-email">
                  {{ user.email }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="gc-topbar-menu-divider" />
            <v-list-item class="gc-topbar-logout-item" @click="logout">
              <v-list-item-icon class="mr-3">
                <v-icon color="#f5c347">mdi-logout</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Sign out</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-app-bar>

      <v-main class="gc-main">
        <router-view />
      </v-main>
    </template>
  </v-app>
</template>

<script>
import { mapState } from 'vuex';
import axios from 'axios';
import LoginScreen from '@/components/LoginScreen.vue';
import TranslationAiOverlay from '@/components/TranslationAiOverlay.vue';
import { setBucketUrlConfig } from '@/plugins/util';
import { AUTH_ENABLED, LOGIN_PAGE_URL, redirectToLogin } from '@/plugins/permissions';

export default {
  name: 'App',

  components: {
    LoginScreen,
    TranslationAiOverlay
  },

  data: () => ({
    bootLoading: true,
    loginMessage: 'Checking session…',
    avatarLoadFailed: false
  }),

  computed: {
    ...mapState(['user', 'aiTranslationOverlay']),
    userDisplayName() {
      if (!this.user) return '';
      return this.user.name || this.user.email || 'Signed in';
    },
    showProfilePhoto() {
      return Boolean(this.user?.picture) && !this.avatarLoadFailed;
    },
    userInitials() {
      if (!this.user) return '';
      const name = this.user.name || '';
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      }
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      const email = this.user.email || '';
      return email.charAt(0).toUpperCase() || '?';
    },
    loginPageUrl() {
      if (!LOGIN_PAGE_URL || LOGIN_PAGE_URL === '/') return '';
      return LOGIN_PAGE_URL;
    }
  },

  head() {
    return {
      title: {
        inner: 'Gaming Corps - Translation Manager'
      }
    };
  },

  watch: {
    user(val) {
      if (val) {
        this.avatarLoadFailed = false;
      }
    }
  },

  methods: {
    goToDashboard() {
      redirectToLogin();
    },
    async loadStorageConfig() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_BACKEND_URL}/storage/config`, {
          withCredentials: true
        });
        const data = res.data || {};
        const buckets = data.buckets || [];
        const map = {};
        buckets.forEach(({ bucket, bucketURL }) => { map[bucket] = bucketURL; });
        setBucketUrlConfig(map);
        this.$store.commit('setBuckets', buckets.map(({ bucket }) => bucket).filter(Boolean));
        this.$store.commit('setTranslationServerEnabled', Boolean(data.translationEnabled));
        this.$store.commit('setCompareServerEnabled', Boolean(data.compareTranslationEnabled));
        this.$store.commit('setLocaleTranslationRuleLocales', data.localeTranslationRules || []);
        this.$store.commit('setKeepEnglishFeatures', data.keepEnglishFeatures || []);
        this.$store.commit('setTranslationConfig', data.translation || null);
      } catch {
        /* interceptor handles 401 */
      }
    },
    async logout() {
      try {
        await axios.post(`${process.env.VUE_APP_BACKEND_URL}/auth/logout`, {}, {
          withCredentials: true
        });
      } catch {
        /* still leave the app */
      }
      this.$store.commit('setUser', null);
      redirectToLogin();
    },
    async bootstrapSession() {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_URL}/auth/login`,
          {},
          { withCredentials: true }
        );
        const payload = response.data || {};
        const user = payload.user;
        if (!user) {
          throw new Error('No session');
        }
        this.$store.commit('setUser', user);
        this.$store.commit('setPermissions', payload.permissions || []);
        this.$store.commit('setPermissionPrefix', payload.permissionPrefix);
        await this.loadStorageConfig();
      } catch (error) {
        this.$store.commit('setUser', null);
        const status = error?.response?.status;
        if (AUTH_ENABLED) {
          this.loginMessage = this.loginPageUrl
            ? 'No session yet. Sign in from the auth gateway, then launch Translation Manager.'
            : 'No session yet. Set VUE_APP_LOGIN_PAGE_URL to the auth gateway.';
          redirectToLogin();
        } else if (status === 401) {
          this.loginMessage =
            'API auth is on, but this app is in local-bypass mode. Set AUTH_ENABLED=false on the API, or set VUE_APP_AUTH_ENABLED=true and open the app from the gateway.';
        } else {
          this.loginMessage = 'Could not start a local session. Check the API is running.';
        }
      } finally {
        this.bootLoading = false;
      }
    }
  },

  mounted() {
    this.bootstrapSession();
  }
};
</script>
